import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Button } from 'reactstrap'
import api from 'api'
import * as classroomActions from 'actions/classroom'
import {
  PORTAL_MY_CLASS_URL,
  ROSTER_REQUIRED_BODY_PREFIX,
  ROSTER_REQUIRED_TITLE,
  ROSTER_REQUIRED_FOOTER_NOTE,
} from 'utils/defaults'
import { authKeys } from 'utils/config'
import styles from './styles.module.scss'

const EMPTY_ROSTER_RETRY_MS = 1000

async function fetchSubscriptionCheck(getAccessTokenSilently) {
  const token = await getAccessTokenSilently({
    authorizationParams: authKeys.authorizationParams,
  })
  const { data } = await api.get('/api/check/subscription', {
    headers: { authorization: `Bearer ${token}` },
  })
  return data
}

function shouldBlockRoster(data) {
  return (
    data?.status === 'ACTIVE' &&
    data?.hasRoster === false &&
    !data?.rosterCheckError
  )
}

const RosterRequiredGate = () => {
  const { getAccessTokenSilently, logout } = useAuth0()
  const dispatch = useDispatch()
  const history = useHistory()
  const [blocked, setBlocked] = useState(false)
  const lastRecheckRef = useRef(0)
  const hasStudents = useSelector(
    (state) =>
      Array.isArray(state.classroom?.students) &&
      state.classroom.students.length > 0
  )
  const hasStudentsRef = useRef(hasStudents)
  hasStudentsRef.current = hasStudents

  const applySubscriptionResult = useCallback(
    (data) => {
      const nextBlocked = shouldBlockRoster(data)
      setBlocked(nextBlocked)
      // A first-load race can report hasRoster:false after the roster fetch
      // already succeeded. Don't wipe students in that case.
      if (nextBlocked && !hasStudentsRef.current) {
        dispatch(classroomActions.clear())
      }
    },
    [dispatch]
  )

  const recheckSubscription = useCallback(async () => {
    try {
      const data = await fetchSubscriptionCheck(getAccessTokenSilently)
      lastRecheckRef.current = Date.now()
      applySubscriptionResult(data)
    } catch {
      setBlocked(false)
    }
  }, [getAccessTokenSilently, applySubscriptionResult])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await fetchSubscriptionCheck(getAccessTokenSilently)
        if (cancelled) return
        if (shouldBlockRoster(data)) {
          await new Promise((resolve) =>
            setTimeout(resolve, EMPTY_ROSTER_RETRY_MS)
          )
          if (cancelled) return
          const retried = await fetchSubscriptionCheck(getAccessTokenSilently)
          if (cancelled) return
          lastRecheckRef.current = Date.now()
          applySubscriptionResult(retried)
        } else {
          lastRecheckRef.current = Date.now()
          applySubscriptionResult(data)
        }
      } catch {
        if (!cancelled) setBlocked(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently, applySubscriptionResult])

  useEffect(() => {
    const throttledRecheck = () => {
      const now = Date.now()
      if (now - lastRecheckRef.current < 1200) return
      lastRecheckRef.current = now
      recheckSubscription()
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        throttledRecheck()
      }
    }

    window.addEventListener('focus', throttledRecheck)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', throttledRecheck)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [recheckSubscription])

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } })
    history.push('/login')
  }

  if (!blocked || hasStudents) return null

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="roster-required-title">
      <div className={styles.modalCard}>
        <h2 id="roster-required-title" className={styles.title}>
          {ROSTER_REQUIRED_TITLE}
        </h2>
        <div className={styles.warning}>
          {ROSTER_REQUIRED_BODY_PREFIX}
          <a
            className={styles.portalLink}
            href={PORTAL_MY_CLASS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {PORTAL_MY_CLASS_URL}
          </a>
        </div>
        <Button
          color="primary"
          className="btn-block mb-2"
          href={PORTAL_MY_CLASS_URL}
          target="_blank"
          rel="noopener noreferrer"
          tag="a"
        >
          Open Portal
        </Button>
        <Button color="secondary" outline className="btn-block" onClick={handleLogout}>
          Log out
        </Button>
        <p className={styles.footerNote}>{ROSTER_REQUIRED_FOOTER_NOTE}</p>
      </div>
    </div>
  )
}

export default RosterRequiredGate
